import { Box, Divider, Stack, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { exportToPDF } from "../utils/exportToPdf";
import { exportToExcel } from "./utils/exportToExcel";
import {
  appliedFiltersType,
  ReportTemplateBodyParams,
  ReportTemplateTypeEnum,
  userActivityReportByPortalType,
  userActivityReportCompleteType,
  userActivityReportUsers,
} from "../../../../services/api/types/reports/reports";
import { useEffect, useState } from "react";
import {
  getReportTemplateById,
  previewReportTemplate,
  runReportTemplate,
} from "../../../../services/api/apiClient";
import { usePortal } from "../../../../context/PortalsContext";
import { useAuth } from "../../../../context/AuthContext";
import { formatDate, formatTime, generateFileName } from "../utils/helpers";
import { Spinner } from "../../../../components/Spinner/Spinner";
import { ExportMenu } from "../components/ExportMenu";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { UserActivityTable } from "./components/UserActivityTable";
import { UserActivityByPortalsTable } from "./components/UserActivityByPortalsTable";
import Logo from "../../../../authentication/components/Logo";
import { useCustomisation } from "../../../../context/CustomisationContext";
import { ExpandableFiltersSection } from "../components/ExpandableFiltersSection";

interface UserActivityReportProps {
  previewTemplateData?: ReportTemplateBodyParams;
  previewPortalId?: string;
  isPreviewMode?: boolean;
}

export const UserActivityReport: React.FC<UserActivityReportProps> = ({
  previewTemplateData,
  previewPortalId,
  isPreviewMode,
}) => {
  const [data, setData] = useState<userActivityReportCompleteType | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<appliedFiltersType>({});
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isGroupedByPortal, setIsGroupedByPortal] = useState(false);
  const [actionOnUser, setActionOnUser] = useState<boolean | undefined>(
    undefined,
  );

  const { portals } = usePortal();
  const { auth } = useAuth();
  const location = useLocation();
  const { portalUid: urlPortalUid } = useParams();
  const [searchParams] = useSearchParams();

  const { customisations } = useCustomisation();
  const {
    GPC: { navbarSettings },
  } = customisations.customisations;

  const gpcNavbarBgColor = navbarSettings?.navbarBgColor || undefined;

  // Check if preview mode: prop OR location.state
  const previewMode =
    isPreviewMode ||
    (previewTemplateData !== undefined && previewPortalId !== undefined) ||
    (location.state as { previewMode?: boolean })?.previewMode ||
    false;

  const currentDate = new Date();
  const formattedDate = formatDate(currentDate);
  const formattedTime = formatTime(currentDate);

  // Get user name from auth context
  const userName = auth.user
    ? `${auth.user.firstName || ""} ${auth.user.lastName || ""}`.trim() ||
      "Unknown User"
    : "Unknown User";

  useEffect(() => {
    if (previewMode) {
      const templateData =
        previewTemplateData ||
        (location.state as { templateData?: ReportTemplateBodyParams })
          ?.templateData;
      const portalId =
        previewPortalId || (location.state as { portalId?: string })?.portalId;

      if (!templateData || !portalId) {
        console.error("Missing template data or portalId in preview mode");
        return;
      }
      fetchPreviewData(portalId, templateData);
    } else {
      // Normal mode: the portal and report template come from this tab's own
      // URL (so a report opened in a new tab always runs against the portal
      // and template it was opened with, not whatever another tab last set
      // in shared storage).
      const reportTemplateId = searchParams.get("reportTemplateId");
      const portalId = urlPortalUid || portals.selectedPortal?.portalUid;

      if (!portalId || !reportTemplateId) {
        console.error("Missing portalId or reportTemplateId");
        return;
      }

      fetchData(portalId, reportTemplateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlPortalUid,
    portals.selectedPortal,
    previewMode,
    previewTemplateData,
    previewPortalId,
    location.state,
    searchParams,
  ]);

  const fetchPreviewData = async (
    portalId: string,
    templateData: ReportTemplateBodyParams,
  ) => {
    if (!portalId || !templateData) return;
    setLoading(true);
    setActionOnUser(templateData.actionOnUser);

    try {
      const response = await previewReportTemplate(portalId, templateData);

      if (!response.finResult) {
        console.error("Response does not have finResult property");
        setData(null);
        setIsGroupedByPortal(false);
        return;
      }

      // Extract appliedFilters from response
      if (response.finResult?.appliedFilters) {
        setAppliedFilters(response.finResult.appliedFilters);
      }

      // Detect structure: if first item has 'users' property, it's grouped by portal
      const firstItem = response.finResult?.reportData?.[0];
      setIsGroupedByPortal(
        firstItem !== undefined &&
          "users" in firstItem &&
          Array.isArray(firstItem.users),
      );

      setData(response as userActivityReportCompleteType);
    } catch (error: any) {
      console.error("Failed to fetch preview data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (portalId: string, reportTemplateId: string) => {
    if (!portalId || !reportTemplateId) return;
    setLoading(true);

    try {
      const [response, template] = await Promise.all([
        runReportTemplate(portalId, reportTemplateId, auth.user?.UserPortalUid || ""),
        getReportTemplateById(portalId, reportTemplateId),
      ]);
      setActionOnUser(template.actionOnUser);

      // Set timestamp to notify Reports page that a report was run
      const now = Date.now();
      const last = parseInt(
        localStorage.getItem("reportRunTimestamp") || "0",
        10,
      );
      const next = now > last ? now : last + 1;
      localStorage.setItem("reportRunTimestamp", next.toString());

      if (!response.finResult) {
        console.error("Response does not have finResult property");
        setData(null);
        setIsGroupedByPortal(false);
        return;
      }

      // Extract appliedFilters from response
      if (response.finResult?.appliedFilters) {
        setAppliedFilters(response.finResult.appliedFilters);
      }

      // Detect structure: if first item has 'users' property, it's grouped by portal
      const firstItem = response.finResult?.reportData?.[0];
      setIsGroupedByPortal(
        firstItem !== undefined &&
          "users" in firstItem &&
          Array.isArray(firstItem.users),
      );

      setData(response as userActivityReportCompleteType);
    } catch (error: any) {
      console.error("Failed to fetch report data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setFiltersExpanded(true); // Ensure filters are expanded before export

    await exportToPDF(
      "user-activity-report",
      generateFileName(ReportTemplateTypeEnum.USER_ACTIVITY_REPORT),
    );
  };

  const handleExportExcel = () => {
    exportToExcel({
      filters: appliedFilters,
      actionOnUser,
      reportData: data?.finResult?.reportData as
        | userActivityReportByPortalType[]
        | userActivityReportUsers[],
      reportName: "User activity report",
      generatedBy: userName,
      time: formattedTime,
      isGroupedByPortal: isGroupedByPortal,
      fileName: generateFileName(ReportTemplateTypeEnum.USER_ACTIVITY_REPORT),
    });
  };

  return loading ? (
    <Spinner />
  ) : (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        py: { xs: 0, md: 4 },
        justifyContent: "center",
      }}
    >
      <Box
        id="user-activity-report"
        sx={{
          backgroundColor: "#fff",
          width: "100%",
          maxWidth: "1280px",
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          sx={{ backgroundColor: gpcNavbarBgColor, p: 1 }}
        >
          <Logo isLoginPageLogo={false} />
          {previewMode ? (
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: "#667085",
              }}
            >
              Preview mode
            </Typography>
          ) : (
            <ExportMenu
              className="export-buttons"
              onExportPDF={handleExportPDF}
              onExportExcel={handleExportExcel}
            />
          )}
        </Stack>

        <Divider sx={{ borderColor: "#5354D8", mb: 2, mt: 3 }} />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="start"
          mb={2}
          sx={{ flexWrap: "wrap", rowGap: 1.5 }}
        >
          <Typography sx={{ color: "#1F2937", fontWeight: 600, fontSize: 16 }}>
            User Activity Report
          </Typography>

          <Stack spacing={0.5} alignItems="start">
            <Box display="flex" alignItems="center" gap={0.75}>
              <CalendarTodayOutlinedIcon
                sx={{ fontSize: 12, color: "#667085" }}
              />
              <Typography sx={{ fontSize: 10, color: "#667085" }}>
                {formattedDate}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={0.75}>
              <AccessTimeOutlinedIcon sx={{ fontSize: 12, color: "#667085" }} />
              <Typography sx={{ fontSize: 10, color: "#667085" }}>
                {formattedTime}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={0.75}>
              <PersonOutlinedIcon sx={{ fontSize: 12, color: "#667085" }} />
              <Typography sx={{ fontSize: 10, color: "#667085" }}>
                {userName}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Divider sx={{ borderColor: "#5354D8", mb: 2 }} />

        {/* Filters Section */}
        <ExpandableFiltersSection
          appliedFilters={appliedFilters}
          actionOnUser={actionOnUser}
          expanded={filtersExpanded}
          onExpandedChange={setFiltersExpanded}
        />

        {/* Table Section */}
        <Typography color="#1F2937" fontSize="14px" fontWeight={500} mb={3}>
          Activity details
        </Typography>
        {isGroupedByPortal ? (
          <UserActivityByPortalsTable
            data={
              data?.finResult?.reportData as userActivityReportByPortalType[]
            }
          />
        ) : (
          <UserActivityTable
            data={data?.finResult?.reportData as userActivityReportUsers[]}
          />
        )}
      </Box>
    </Box>
  );
};
